import { Store, StoreComponentStatus } from "../types";
import { fetchWithTimeout } from "./http";

const urlStores = process.env.REACT_APP_API_NODE_URL + "stores/";
const urlMirror = process.env.REACT_APP_API_NODE_URL + "stores/mirror/";
const urlSearchIndex = process.env.REACT_APP_API_NODE_URL + "stores/search-index/";
const urlDeploymentBranches = process.env.REACT_APP_API_NODE_URL + "stores/deployment-branches/";
const urlNetlifySite = process.env.REACT_APP_API_NODE_URL + "stores/netlify-site/";

const PING_TIMEOUT_MS = 8000;
const DELETE_TIMEOUT_MS = 45000;

const STOREFRONT_API_VERSION = "2026-01";

const isTimeout = (error: unknown) => (error as Error)?.name === "AbortError";

const runDelete = async (url: string, noun: string): Promise<string> => {
    try {
        const res = await fetchWithTimeout(
            url,
            { method: 'DELETE', credentials: 'include' },
            DELETE_TIMEOUT_MS
        );

        if (res.ok) return "";

        const data = await res.json().catch(() => null);
        return data?.message || `Could not delete the ${noun} (${res.status}).`;
    } catch (error) {
        if (isTimeout(error)) {
            return `Deleting the ${noun} took too long to answer. It may still be running — try again in a moment.`;
        }

        return `Could not delete the ${noun}. Please try again later.`;
    }
};

const hostOf = (storeUrl: string | null | undefined) =>
    (storeUrl ?? "").trim().replace(/^https?:\/\//, "").replace(/\/+$/, "");

export const shopifyAppsUrl = (storeUrl: string) =>
    `https://${hostOf(storeUrl)}/admin/settings/apps`;

const tokenOf = (token: string | null | undefined) => {
    const value = (token ?? "").trim();
    return value === "null" ? "" : value;
};

export const pingShopifyStore = async (store: Store): Promise<StoreComponentStatus> => {
    const host = hostOf(store.storeUrl);
    const storefrontToken = tokenOf(store.hydrogenStorefrontToken);
    if (!host || !storefrontToken) return "unhealthy";

    try {
        const response = await fetchWithTimeout(
            `https://${host}/api/${STOREFRONT_API_VERSION}/graphql.json`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Shopify-Storefront-Access-Token": storefrontToken,
                },
                body: JSON.stringify({ query: "{ shop { name } }" }),
            },
            PING_TIMEOUT_MS
        );

        if (response.status === 404) return "deleted";
        if (!response.ok) return "unhealthy";

        const payload = await response.json();
        return payload?.data?.shop?.name ? "healthy" : "unhealthy";
    } catch {
        return "unhealthy";
    }
};

export const getMirrorHealth = async (storeId: number): Promise<StoreComponentStatus> => {
    try {
        const res = await fetchWithTimeout(
            `${urlMirror}${storeId}`,
            { method: 'GET', credentials: 'include', cache: 'no-store' },
            PING_TIMEOUT_MS
        );

        if (!res.ok && res.status !== 404) return "unhealthy";

        const payload = await res.json().catch(() => null);

        if (payload?.hasData === true) return "healthy";
        if (payload?.hasData === false) return "deleted";

        return "unhealthy";
    } catch {
        return "unhealthy";
    }
};

export const deleteStoreMirror = (storeId: number): Promise<string> =>
    runDelete(`${urlMirror}${storeId}`, "mirror");

export const getSearchIndexHealth = async (storeId: number): Promise<StoreComponentStatus> => {
    try {
        const res = await fetchWithTimeout(
            `${urlSearchIndex}${storeId}`,
            { method: 'GET', credentials: 'include', cache: 'no-store' },
            PING_TIMEOUT_MS
        );

        if (res.ok) return "healthy";
        if (res.status === 404) return "deleted";
        return "unhealthy";
    } catch {
        return "unhealthy";
    }
};

export const deleteStore = (storeId: number): Promise<string> =>
    runDelete(`${urlStores}${storeId}/complete`, "store");

export type DeploymentRepository = "basic-theme" | "hydrogen-v2";

export type DeploymentBranchesResult = {
    status: StoreComponentStatus;
    branchCount: number;
};

export const getDeploymentBranches = async (
    storeId: number,
    repository: DeploymentRepository
): Promise<DeploymentBranchesResult> => {
    try {
        const res = await fetchWithTimeout(
            `${urlDeploymentBranches}${storeId}/${repository}`,
            { method: 'GET', credentials: 'include', cache: 'no-store' },
            PING_TIMEOUT_MS
        );

        if (res.status === 404) return { status: "deleted", branchCount: 0 };
        if (!res.ok) return { status: "unhealthy", branchCount: 0 };

        const data = await res.json();
        const branchCount = Number(data?.branchCount) || 0;

        return { status: branchCount > 0 ? "healthy" : "deleted", branchCount };
    } catch {
        return { status: "unhealthy", branchCount: 0 };
    }
};

export const deleteSearchIndex = (storeId: number): Promise<string> =>
    runDelete(`${urlSearchIndex}${storeId}`, "search index");

export type NetlifySiteResult = {
    status: StoreComponentStatus;
    name: string | null;
    adminUrl: string | null;
};

export const getNetlifySite = async (storeId: number): Promise<NetlifySiteResult> => {
    const nothing = { name: null, adminUrl: null };

    try {
        const res = await fetchWithTimeout(
            `${urlNetlifySite}${storeId}`,
            { method: 'GET', credentials: 'include', cache: 'no-store' },
            PING_TIMEOUT_MS
        );

        if (!res.ok) return { status: "unhealthy", ...nothing };

        const payload = await res.json().catch(() => null);

        if (payload?.found === true) {
            return {
                status: "healthy",
                name: payload.name ?? null,
                adminUrl: payload.adminUrl ?? null,
            };
        }

        if (payload?.found === false) {
            return { status: "deleted", name: payload.name ?? null, adminUrl: null };
        }

        return { status: "unhealthy", ...nothing };
    } catch {
        return { status: "unhealthy", ...nothing };
    }
};
